// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xAutoCompleteSearchSuggestionsActions } from "@eisinc/App/Store/Reducers/xAutoCompleteSearchSuggestions";
import getBoldedText from "@insite/client-framework/Common/Utilities/getPatternBolded";
import {
    ApiHandler,
    HasOnSuccess,
    markSkipOnCompleteIfOnSuccessIsSet,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import { AutocompleteApiParameter, autocompleteSearch } from "@insite/client-framework/Services/AutocompleteService";
import {
    chain,
    ExecuteOnSuccessCallback,
    RequestDataFromApi,
    SetDisplayTitles,
} from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import { AutocompleteModel } from "@insite/client-framework/Types/ApiModels";

type HandlerType = ApiHandler<AutocompleteApiParameter & HasOnSuccess<AutocompleteModel>, AutocompleteModel>;

export const xRequestDataFromApi: any = async (props: any) => {
    // XNG-Change use 2 as min length instead of 3
    if (
        props.apiParameter.searchOnTyping ||
        props.apiParameter.query.length >= 2 ||
        props.apiParameter.isQuerySuggestionEnabled
    ) {
        props.apiResult = await autocompleteSearch(props.apiParameter);
    }
};

// xng-change: be sure that getBoldedText params is not null
export const xSetDisplayTitles: typeof SetDisplayTitles = props => {
    if (props.apiResult) {
        if (props.apiResult.products) {
            props.apiResult.products.forEach(product => {
                product.displayTitle = getBoldedText(product.title || "", props.apiParameter.query);
                product.displayErpNumber = getBoldedText(product.erpNumber || "", props.apiParameter.query);
                product.displayName = getBoldedText(product.name || "", props.apiParameter.query);
                product.displayManufacturerItemNumber = getBoldedText(
                    product.manufacturerItemNumber || "",
                    props.apiParameter.query,
                );
            });
        }
        if (props.apiResult.suggestions) {
            props.apiResult.suggestions.forEach(suggestion => {
                suggestion.displayTitle = getBoldedText(suggestion.title || "", props.apiParameter.query);
            });
        }
        if (props.apiResult.popularBrands) {
            props.apiResult.popularBrands.forEach(brand => {
                brand.displayTitle = getBoldedText(brand.title || "", props.apiParameter.query);
                if (brand.productLineName) {
                    brand.displayProductLineName = getBoldedText(brand.productLineName || "", props.apiParameter.query);
                }
            });
        }
        if (props.apiResult.brands) {
            props.apiResult.brands.forEach(brand => {
                brand.displayTitle = getBoldedText(brand.title || "", props.apiParameter.query);
                if (brand.productLineName) {
                    brand.displayProductLineName = getBoldedText(brand.productLineName || "", props.apiParameter.query);
                }
            });
        }
        if (props.apiResult.content) {
            props.apiResult.content.forEach(content => {
                content.displayTitle = getBoldedText(content.title || "", props.apiParameter.query);
            });
        }
        if (props.apiResult.categories) {
            props.apiResult.categories.forEach(category => {
                category.displayTitle = getBoldedText(category.title || "", props.apiParameter.query);
                if (category.subtitle) {
                    category.displaySubtitle = getBoldedText(category.subtitle || "", props.apiParameter.query);
                }
            });
        }
        if (props.apiResult.popularCategories) {
            props.apiResult.popularCategories.forEach(category => {
                category.displayTitle = getBoldedText(category.title || "", props.apiParameter.query);
                if (category.subtitle) {
                    category.displaySubtitle = getBoldedText(category.subtitle || "", props.apiParameter.query);
                }
            });
        }
    }
};

export const xExecuteOnSuccessCallback: any = (props: any) => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.(props.apiResult);
    props.dispatch({
        type: "xAutoCompleteSearchSuggestions/CompleteLoad",
        xAutoCompleteSearchSuggestions: props.apiResult,
    }) as xAutoCompleteSearchSuggestionsActions;
};

replaceInChain(chain, RequestDataFromApi, xRequestDataFromApi);
replaceInChain(chain, SetDisplayTitles, xSetDisplayTitles);
replaceInChain(chain, ExecuteOnSuccessCallback, xExecuteOnSuccessCallback);
